// ABOUTME: Minimal top bar for creator blog pages (/@username/*)
// ABOUTME: Shows minimal Drafty branding - prioritizes creator's content
// ABOUTME: Shows Subscribe button for visitors instead of Sign Up
// ABOUTME: Scrolls away when user scrolls down, reappears when scrolling up

"use client"

import { useState, useEffect, useRef } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBell, faPlus, faArrowLeft } from "@fortawesome/free-solid-svg-icons"
import { CreateMenu } from "./create-menu"
import { useAddLinkModal } from "@/contexts/add-link-modal-context"
import { SubscribeModal } from "@/components/subscribe-modal"

const SCROLL_THRESHOLD = 100 // Hide after scrolling this many pixels

interface CreatorTopBarProps {
  isAuthenticated: boolean
  username?: string // Current user's username (not the page owner)
  unreadNotificationsCount?: number
}

// Extract publisher username from pathname like /@username or /@username/post-slug
function getPublisherUsername(pathname: string): string | null {
  const match = pathname.match(/^\/@([^/]+)/)
  return match ? decodeURIComponent(match[1]) : null
}

export function CreatorTopBar({ isAuthenticated, username, unreadNotificationsCount = 0 }: CreatorTopBarProps) {
  const pathname = usePathname()
  const [createMenuOpen, setCreateMenuOpen] = useState(false)
  const [subscribeModalOpen, setSubscribeModalOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const lastScrollY = useRef(0)
  const { openAddLinkModal } = useAddLinkModal()

  const publisherUsername = getPublisherUsername(pathname)
  const isOwnPage = username && publisherUsername && username.toLowerCase() === publisherUsername.toLowerCase()

  // Handle scroll to show/hide the top bar
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      // Always show at top of page
      if (currentScrollY < SCROLL_THRESHOLD) {
        setIsVisible(true)
        lastScrollY.current = currentScrollY
        return
      }

      // Scrolling down - hide
      if (currentScrollY > lastScrollY.current) {
        setIsVisible(false)
      }
      // Scrolling up - show
      else if (currentScrollY < lastScrollY.current) {
        setIsVisible(true)
      }

      lastScrollY.current = currentScrollY
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const handleOpenAddLink = () => {
    setCreateMenuOpen(false)
    openAddLinkModal()
  }

  return (
    <>
      <header
        className={`md:hidden fixed top-0 left-0 right-0 h-14 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm border-b border-gray-200/50 dark:border-neutral-800/50 z-50 transition-transform duration-300 ${
          isVisible ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <div className="h-full px-4 flex items-center justify-between">
          {/* Left: Back to feed (for logged in) or empty */}
          <div className="flex items-center">
            {isAuthenticated && (
              <Link
                href="/home"
                className="flex items-center gap-2 text-gray-500 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4" />
                <span className="text-sm font-medium">Feed</span>
              </Link>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            {isAuthenticated && (
              <>
                {/* Notifications Bell */}
                <Link
                  href="/notifications"
                  className="relative w-10 h-10 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-neutral-800 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                >
                  <FontAwesomeIcon icon={faBell} className="w-5 h-5" />
                  {unreadNotificationsCount > 0 && (
                    <span className="absolute top-1 right-1 min-w-[16px] h-[16px] flex items-center justify-center px-1 text-[10px] font-medium text-white bg-pink-600 rounded-full">
                      {unreadNotificationsCount > 99 ? "99+" : unreadNotificationsCount}
                    </span>
                  )}
                </Link>

                {/* Create Button */}
                <div className="relative">
                  <button
                    onClick={() => setCreateMenuOpen(!createMenuOpen)}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium"
                  >
                    <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}

            {!isAuthenticated && (
              <div className="flex items-center gap-2">
                <Link
                  href="/sign-in"
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                >
                  Sign In
                </Link>
                {/* Subscribe button for visitors - opens modal */}
                {publisherUsername && (
                  <button
                    onClick={() => setSubscribeModalOpen(true)}
                    className="px-4 py-2 text-sm font-medium bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Subscribe
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Create Menu (Bottom Sheet) */}
      {isAuthenticated && (
        <CreateMenu
          isOpen={createMenuOpen}
          onClose={() => setCreateMenuOpen(false)}
          onOpenAddLink={handleOpenAddLink}
          isMobile={true}
        />
      )}

      {/* Subscribe Modal for visitors */}
      {publisherUsername && !isOwnPage && (
        <SubscribeModal
          isOpen={subscribeModalOpen}
          onClose={() => setSubscribeModalOpen(false)}
          publisherId="" // Will be fetched by username
          publisherUsername={publisherUsername}
        />
      )}
    </>
  )
}
